import isEqual from 'lodash/isEqual'
import { useCallback, useMemo } from 'react'
import createPersistedState from 'use-persisted-state'
import { useStore } from '../providers/store'
import { formatGroupCookieName } from '../utils'
import { DIVIDER } from '../utils/cookies'
import { useMemoCompare } from './useMemoCompare'
import { useSessionId } from './useSessionId'

const getFormattedKey = (groupId, key, session, sessionId) =>
	formatGroupCookieName(groupId, key) + (session && sessionId ? `${DIVIDER}${sessionId}` : '')

/**
 * @description Delete old storage keys from localStorage matching the pattern: `groupId|key|*`
 * @param {string} groupId - store group id
 * @param {string} key - localStorage key
 * @param {boolean} session - if true, session id will be added to the key
 * @param {string} sessionId - session id
 * @returns {void}
 */
const deleteOldStorageKeys = (groupId, key, session, sessionId = '') => {
	const validKey = getFormattedKey(groupId, key, session, sessionId)
	const keys = Object.keys(window.localStorage)?.filter((k) => k.startsWith(formatGroupCookieName(groupId, key))) ?? []

	keys.forEach((k) => k !== validKey && window.localStorage.removeItem(k))
}

/**
 * @description Set and read a value from session storage
 * @param {string} key - localStorage key
 * @param {any} [initialValue] - initial value
 * @param {boolean} [session] - if true, session id will be added to the key
 * @returns {[string, function]} [value, setValue]
 **/
export const useLocalStorageByGroup = (key, initialValue, session = false) => {
	const { groupId } = useStore()
	const sessionId = useSessionId()

	const useLocalStorageKey = createPersistedState(getFormattedKey(groupId, key, session, sessionId))
	const [storageValue, setStorageValue] = useLocalStorageKey({})
	const { value: value = initialValue, expireTime } = storageValue || {}

	const memoValue = useMemo(
		() => (expireTime && new Date().getTime() > new Date(expireTime).getTime() ? initialValue : value),
		[value, initialValue, expireTime]
	)

	const parsedValue = useMemoCompare(memoValue, (prev, next) => {
		if (expireTime && new Date().getTime() > new Date(expireTime).getTime()) {
			return false
		}
		return isEqual(prev, next)
	})

	const handleSetValue = useCallback(
		(value, expireTime) => {
			if (expireTime && !(expireTime instanceof Date)) {
				throw new Error('expireTime must be a Date')
			}
			if (typeof value === 'function') {
				value = value(parsedValue)
			}

			setStorageValue({ value, expireTime })
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[parsedValue]
	)

	return [parsedValue, handleSetValue]
}

/**
 * @description Get value from localStorage by key
 * @returns {function} getLocalStorageByGroup(session: boolean, key: string): any
 */
export const useLocalStorageByGroupGet = () => {
	const { groupId } = useStore()
	const sessionId = useSessionId()

	return useCallback(
		(session = false, key) => {
			if (typeof window === 'undefined') {
				return null
			}

			try {
				const item = window.localStorage.getItem(getFormattedKey(groupId, key, session, sessionId))
				const parsedItem = item ? JSON.parse(item) : null

				// Delete invalid keys
				deleteOldStorageKeys(groupId, key, session, sessionId)

				if (parsedItem && parsedItem.expireTime && new Date().getTime() > new Date(parsedItem.expireTime).getTime()) {
					window.localStorage.removeItem(getFormattedKey(groupId, key, session, sessionId))
					return null
				}
				return parsedItem?.value ?? null
			} catch (error) {
				console.warn(`Error reading localStorage key “${key}”:`, error)
				return null
			}
		},
		[groupId, sessionId]
	)
}

/**
 * @description Set value to localStorage by key
 * @returns {function} setLocalStorageByGroup(session: boolean, key: string, value: any, expireTime: Date): void
 */
export const useLocalStorageByGroupSet = () => {
	const { groupId } = useStore()
	const sessionId = useSessionId()

	return useCallback(
		(session = false, key, value, expireTime) => {
			if (expireTime && !(expireTime instanceof Date)) {
				throw new Error('expireTime must be a Date')
			}

			if (typeof window == 'undefined') {
				console.warn(`Tried setting localStorage key “${key}” even though environment is not a client`)
			}

			try {
				// Save to local storage
				window.localStorage.setItem(
					getFormattedKey(groupId, key, session, sessionId),
					JSON.stringify({ value, expireTime })
				)
			} catch (error) {
				console.warn(`Error setting localStorage key “${key}”:`, error)
			}
		},
		[groupId, sessionId]
	)
}

/**
 * @description Remove value from localStorage by key
 * @returns {function} removeLocalStorageByGroup(session: boolean, key: string): void
 */
export const useLocalStorageByGroupRemove = () => {
	const { groupId } = useStore()
	const sessionId = useSessionId()

	return useCallback(
		(session = false, key) => {
			if (typeof window == 'undefined') {
				console.warn(`Tried removing localStorage key “${key}” even though environment is not a client`)
			}

			try {
				// Remove from local storage
				window.localStorage.removeItem(getFormattedKey(groupId, key, session, sessionId))
			} catch (error) {
				console.warn(`Error removing localStorage key “${key}”:`, error)
			}
		},
		[groupId, sessionId]
	)
}
